import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { Avatar, SeloParabenizado, CartaoAniversarioModal } from "./EndomarketingPecas";
import {
  type Colaborador,
  MESES,
  abrirWhatsApp,
  aplicarVariaveis,
  copiar,
  fmt,
  norm,
  primeiroNome,
  temTelefone,
} from "../endomarketing-utils";
import { IconSearch, IconCopy, IconStar, IconAward } from "../icons";
import { compararNomes, ordenarTextos } from "../ordem";

/** Dia do mês primeiro; no mesmo dia, nome em ordem alfabética. */
function porDiaENome(a: Colaborador, b: Colaborador): number {
  const da = Number(a.dia);
  const db = Number(b.dia);
  const dia = (Number.isFinite(da) ? da : 99) - (Number.isFinite(db) ? db : 99);
  return dia || compararNomes(a.nome, b.nome);
}

/**
 * ANIVERSARIANTES do mês — nascimento e tempo de casa.
 *
 * O que a tela resolve, além de listar:
 *   • separa HOJE do resto do mês (é o que exige ação agora);
 *   • a mensagem é EDITÁVEL e vale para todos, com {nome} trocado por pessoa —
 *     assim o tom é o mesmo e ninguém reescreve o texto dez vezes;
 *   • marca quem já foi parabenizado no ano (gravado no servidor, porque mais
 *     de uma pessoa do RH usa o sistema);
 *   • monta o POST (cartão do sistema, com fundo salvo, ou imagem pronta) e
 *     manda pelo WhatsApp — para a pessoa ou para um grupo.
 */

const MSG_NASCIMENTO =
  "Parabéns, {nome}! 🎉\n\nA equipe SERCAMP deseja um feliz aniversário e um ano muito especial. Obrigado por fazer parte do nosso time!";
const MSG_EMPRESA =
  "Parabéns, {nome}! 🎊\n\nHoje você completa mais um ano de SERCAMP. Obrigado pela dedicação e por caminhar com a gente!";

export default function Aniversariantes({ onAviso }: { onAviso: (m: string) => void }) {
  const hojeData = new Date();
  const [mes, setMes] = useState(hojeData.getMonth() + 1);
  const [dados, setDados] = useState<any>(null);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [setor, setSetor] = useState("");
  const [msgNasc, setMsgNasc] = useState(MSG_NASCIMENTO);
  const [msgEmp, setMsgEmp] = useState(MSG_EMPRESA);
  /**
   * Post aberto. O `tipo` vai junto porque o mesmo editor serve ao aniversário
   * e ao tempo de casa — e o parabéns marcado depois do envio tem de ser o certo.
   */
  const [cartao, setCartao] = useState<{
    pessoa: Colaborador;
    legenda: string;
    titulo?: string;
    msg: string;
    tipo: "nascimento" | "empresa";
  } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setDados(await api.endomarketing.aniversariantes(mes));
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Recarrega sem trocar a lista por "Carregando…" — usado com o post aberto
   * por cima, para o selo de "parabenizado" aparecer sem a tela piscar.
   */
  const recarregarQuieto = useCallback(async () => {
    try {
      setDados(await api.endomarketing.aniversariantes(mes));
    } catch {
      /* a próxima carga normal mostra o erro */
    }
  }, [mes]);

  /** O post foi enviado PARA A PESSOA: marca o parabéns do tipo certo. */
  async function postEnviado(p: Colaborador, tipo: "nascimento" | "empresa") {
    await api.endomarketing.parabens(p.id, tipo).catch(() => {});
    await recarregarQuieto();
  }

  // A ordem natural aqui é o DIA do mês; o nome (A→Z) só desempata quem faz
  // aniversário no mesmo dia. Cópias: os dados recebidos não são mexidos.
  const nasc: Colaborador[] = useMemo(
    () => [...((dados?.aniversariantes ?? []) as Colaborador[])].sort(porDiaENome),
    [dados],
  );
  const casa: Colaborador[] = useMemo(
    () => [...((dados?.tempo_de_casa ?? []) as Colaborador[])].sort(porDiaENome),
    [dados],
  );

  const setores = useMemo(
    () => ordenarTextos([...new Set([...nasc, ...casa].map((c) => c.departamento).filter(Boolean))] as string[]),
    [nasc, casa],
  );

  const filtrar = useCallback(
    (lista: Colaborador[]) => {
      const q = norm(busca);
      return lista.filter(
        (c) =>
          (!setor || c.departamento === setor) &&
          (!q || norm(c.nome).includes(q) || norm(c.cargo).includes(q)),
      );
    },
    [busca, setor],
  );

  const nascFiltrado = useMemo(() => filtrar(nasc), [nasc, filtrar]);
  const casaFiltrado = useMemo(() => filtrar(casa), [casa, filtrar]);

  // Só faz sentido separar "hoje" quando o mês na tela é o mês corrente.
  const ehMesAtual = mes === hojeData.getMonth() + 1;
  const diaHoje = hojeData.getDate();
  const deHoje = ehMesAtual ? nascFiltrado.filter((c) => c.dia === diaHoje) : [];
  const doMes = ehMesAtual ? nascFiltrado.filter((c) => c.dia !== diaHoje) : nascFiltrado;

  async function parabenizar(p: Colaborador, tipo: "nascimento" | "empresa") {
    const texto = aplicarVariaveis(tipo === "empresa" ? msgEmp : msgNasc, p);
    if (!abrirWhatsApp(p.telefone, texto)) {
      onAviso(`${p.nome} está sem telefone no cadastro — preencha em "Fotos da equipe".`);
      return;
    }
    await api.endomarketing.parabens(p.id, tipo).catch(() => {});
    await carregar();
  }

  async function alternarMarca(p: Colaborador, tipo: "nascimento" | "empresa") {
    await api.endomarketing.parabens(p.id, tipo, !!p.parabenizado).catch(() => {});
    await carregar();
  }

  async function copiarLista() {
    const linhas = [
      `Aniversariantes de ${MESES[mes - 1]}:`,
      ...nascFiltrado.map((c) => `${String(c.dia).padStart(2, "0")} — ${c.nome}`),
    ];
    if (casaFiltrado.length) {
      linhas.push("", "Aniversário de empresa:");
      linhas.push(
        ...casaFiltrado.map((c) => `${String(c.dia).padStart(2, "0")} — ${c.nome} (${c.anos} ano(s))`),
      );
    }
    onAviso((await copiar(linhas.join("\n"))) ? "Lista copiada." : "Não foi possível copiar.");
  }

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  const faltamTelefone = nascFiltrado.filter((c) => !temTelefone(c)).length;

  return (
    <>
      <div className="crud-toolbar endo-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar pessoa ou cargo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <label className="endo-mes">
          <span className="muted">Mês</span>
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </option>
            ))}
          </select>
        </label>

        <label className="endo-mes">
          <span className="muted">Setor</span>
          <select value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">Todos</option>
            {setores.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <div className="toolbar-right">
          <span className="badge">{nascFiltrado.length} aniversariante(s)</span>
          <span className="badge">{casaFiltrado.length} de empresa</span>
          <button className="btn-secondary btn-sm" onClick={copiarLista}>
            <IconCopy size={15} /> Copiar lista
          </button>
        </div>
      </div>

      {(dados?.sem_data_nascimento ?? 0) > 0 && (
        <div className="endo-alerta">
          {dados.sem_data_nascimento} colaborador(es) sem <strong>data de nascimento</strong> — eles
          não aparecem aqui. Preencha na aba <strong>Fotos da equipe</strong>.
        </div>
      )}
      {faltamTelefone > 0 && (
        <div className="endo-alerta">
          {faltamTelefone} aniversariante(s) sem <strong>telefone</strong>: dá para gerar o cartão
          e mandar num grupo, mas não abrir a conversa da pessoa.
        </div>
      )}

      {/* A mensagem vale para toda a lista — muda aqui e vale para todos. */}
      <details className="endo-msg">
        <summary>Mensagem que será enviada (clique para ajustar)</summary>
        <div className="endo-msg-grade">
          <label className="field">
            <span>Aniversário</span>
            <textarea rows={4} value={msgNasc} onChange={(e) => setMsgNasc(e.target.value)} />
          </label>
          <label className="field">
            <span>Aniversário de empresa</span>
            <textarea rows={4} value={msgEmp} onChange={(e) => setMsgEmp(e.target.value)} />
          </label>
        </div>
        <small className="muted">
          Use <code>{"{nome}"}</code>, <code>{"{setor}"}</code> e <code>{"{cargo}"}</code> — o
          sistema troca por pessoa na hora de abrir o WhatsApp.
        </small>
      </details>

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : nascFiltrado.length === 0 && casaFiltrado.length === 0 ? (
        <div className="empty">Ninguém faz aniversário em {MESES[mes - 1]} com esses filtros.</div>
      ) : (
        <>
          {deHoje.length > 0 && (
            <Secao
              titulo="Hoje"
              Icon={IconStar}
              lista={deHoje}
              destaque
              rodape={(c) => `${String(c.dia).padStart(2, "0")} de ${MESES[mes - 1]} · hoje!`}
              onParabenizar={(c) => parabenizar(c, "nascimento")}
              onMarcar={(c) => alternarMarca(c, "nascimento")}
              onCartao={(c) =>
                setCartao({
                  pessoa: c,
                  legenda: c.fazendo ? `${c.fazendo} anos` : "Parabéns!",
                  msg: aplicarVariaveis(msgNasc, c),
                  tipo: "nascimento",
                })
              }
            />
          )}

          {doMes.length > 0 && (
            <Secao
              titulo={deHoje.length > 0 ? `Resto de ${MESES[mes - 1]}` : `Aniversariantes de ${MESES[mes - 1]}`}
              Icon={IconStar}
              lista={doMes}
              rodape={(c) => `${String(c.dia).padStart(2, "0")} de ${MESES[mes - 1]}`}
              onParabenizar={(c) => parabenizar(c, "nascimento")}
              onMarcar={(c) => alternarMarca(c, "nascimento")}
              onCartao={(c) =>
                setCartao({
                  pessoa: c,
                  legenda: c.fazendo ? `${c.fazendo} anos` : "Parabéns!",
                  msg: aplicarVariaveis(msgNasc, c),
                  tipo: "nascimento",
                })
              }
            />
          )}

          {casaFiltrado.length > 0 && (
            <Secao
              titulo="Aniversário de empresa"
              Icon={IconAward}
              lista={casaFiltrado}
              rodape={(c) => `${c.anos} ano(s) de casa · desde ${fmt(c.data_admissao)}`}
              onParabenizar={(c) => parabenizar(c, "empresa")}
              onMarcar={(c) => alternarMarca(c, "empresa")}
              onCartao={(c) =>
                setCartao({
                  pessoa: c,
                  legenda: `${c.anos} ano(s) de SERCAMP`,
                  // O título existia na Secao mas nunca chegava ao cartão: o
                  // post de tempo de casa saía com "Feliz aniversário!".
                  titulo: "Parabéns pelo tempo de casa!",
                  msg: aplicarVariaveis(msgEmp, c),
                  tipo: "empresa",
                })
              }
            />
          )}
        </>
      )}

      {cartao && (
        <CartaoAniversarioModal
          pessoa={cartao.pessoa}
          legenda={cartao.legenda}
          titulo={cartao.titulo}
          mensagem={cartao.msg}
          onEnviado={() => postEnviado(cartao.pessoa, cartao.tipo)}
          onClose={() => setCartao(null)}
        />
      )}
    </>
  );
}

function Secao({
  titulo,
  Icon,
  lista,
  rodape,
  destaque,
  onParabenizar,
  onMarcar,
  onCartao,
}: {
  titulo: string;
  Icon: typeof IconStar;
  lista: Colaborador[];
  rodape: (c: Colaborador) => string;
  destaque?: boolean;
  onParabenizar: (c: Colaborador) => void;
  onMarcar: (c: Colaborador) => void;
  onCartao: (c: Colaborador) => void;
}) {
  return (
    <section className="endo-secao">
      <h3 className="endo-titulo">
        <Icon size={16} /> {titulo} <span className="badge">{lista.length}</span>
      </h3>
      <div className="endo-cards">
        {lista.map((c) => (
          <article key={c.id} className={`endo-card ${destaque ? "hoje" : ""}`}>
            <Avatar pessoa={c} tamanho="m" />
            <div className="endo-card-info">
              <strong>{c.nome}</strong>
              <small className="muted">{c.cargo || c.departamento || "—"}</small>
              <span className="endo-dia">{rodape(c)}</span>
            </div>
            <div className="endo-card-acoes">
              {c.parabenizado ? (
                <SeloParabenizado onDesfazer={() => onMarcar(c)} />
              ) : (
                <button
                  className="btn-whats btn-sm"
                  onClick={() => onParabenizar(c)}
                  title={temTelefone(c) ? `Abrir o WhatsApp de ${primeiroNome(c.nome)}` : "Sem telefone no cadastro"}
                >
                  Parabenizar
                </button>
              )}
              <button
                className="btn-secondary btn-sm"
                onClick={() => onCartao(c)}
                title="Montar o post (cartão ou imagem pronta) e enviar pelo WhatsApp"
              >
                Cartão
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
